import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { ASTERISK_API_BASE } from '../../config/bridge';

const safeJsonParse = (value, fallback) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch (_error) {
    return fallback;
  }
};

const getAgentName = (agent = {}) =>
  String(
    agent.displayName ||
    agent.fullName ||
    agent.name ||
    agent.username ||
    agent.extension ||
    'Agent'
  ).trim();

const getAgentExtension = (agent = {}) =>
  String(agent.extension || agent.ext || '').trim();

const getAgentMeta = (agent = {}) => {
  const username = String(agent.username || '').trim();
  const extension = getAgentExtension(agent);
  if (username && extension) {
    return `${username} (${extension})`;
  }
  return extension || username || '';
};

const ChatAgentSelector = ({ onSelectAgent, onClose, selectedAgent }) => {
  const { startAgentChat, agentDirectoryVersion, unreadCounts, getSessionSummary } = useChat();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingAgentId, setPendingAgentId] = useState('');

  const currentUser = useMemo(() => {
    const userData = safeJsonParse(localStorage.getItem('user'), {});
    const extensionData = safeJsonParse(localStorage.getItem('extension'), {});

    return {
      userId: String(userData.UserID || userData.userId || userData.ID || userData.id || '').trim(),
      username: String(userData.UserName || userData.username || userData.LoginName || userData.loginName || 'Agent').trim(),
      displayName: String(userData.FullName || userData.UserName || userData.username || 'Agent').trim(),
      extension: String(extensionData.extensionNumber || extensionData.extension || '').trim()
    };
  }, []);

  const fetchAgents = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${ASTERISK_API_BASE}/chat/agents?ts=${Date.now()}`, {
        cache: 'no-store',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        const currentExtension = currentUser.extension;
        const currentUserId = currentUser.userId;
        const allAgents = (data.agents || []).filter((agent) => {
          const ext = String(agent.extension || '').trim();
          const isCurrentAgent = ext === currentExtension || ext === currentUserId;
          return !isCurrentAgent;
        }).sort((first, second) => {
          if (Boolean(first.online) !== Boolean(second.online)) {
            return first.online ? -1 : 1;
          }
          return getAgentName(first).localeCompare(getAgentName(second));
        });

        setAgents(allAgents);
      } else {
        setAgents([]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [currentUser.extension, currentUser.userId]);

  useEffect(() => {
    fetchAgents();
  }, [agentDirectoryVersion, fetchAgents]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchAgents({ silent: true });
    }, 5000);

    const handleWindowFocus = () => {
      fetchAgents({ silent: true });
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleWindowFocus);
    };
  }, [fetchAgents]);

  const filteredAgents = agents.filter((agent) => {
    const haystack = [
      agent.displayName,
      agent.username,
      agent.extension
    ].join(' ').toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  });

  const unreadCountsByAgent = useMemo(() => {
    return Object.entries(unreadCounts).reduce((counts, [sessionId, unreadCount]) => {
      const count = Number(unreadCount) || 0;
      const peerId = String(getSessionSummary(sessionId)?.peerId || '').trim();
      if (count > 0 && peerId) {
        counts[peerId] = (counts[peerId] || 0) + count;
      }
      return counts;
    }, {});
  }, [getSessionSummary, unreadCounts]);

  const handleSelectAgent = async (agent) => {
    const selfAgentId = currentUser.extension || currentUser.userId;
    if (!selfAgentId) {
      alert('Current agent identity not found.');
      return;
    }

    try {
      setPendingAgentId(agent.extension);
      const result = await startAgentChat(selfAgentId, agent.extension);
      const sessionId = result?.sessionId || result?.SessionId;
      if (sessionId) {
        onSelectAgent(agent, sessionId);
      }
    } catch (error) {
      console.error('Failed to start agent chat:', error);
      alert(error.message || 'Failed to start chat session');
    } finally {
      setPendingAgentId('');
    }
  };

  if (loading) {
    return (
      <div className="chat-agent-selector p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-1 fw-semibold">Start Internal Chat</h6>
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>
              Loading agent list
            </div>
          </div>
          <button className="btn btn-close" onClick={onClose}></button>
        </div>
        <div className="text-center py-4">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-agent-selector p-4" style={{ maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-1 fw-semibold">Start Internal Chat</h6>
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
            Select the agent you want to message
          </div>
        </div>
        <button className="btn btn-close" onClick={onClose}></button>
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search agents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ borderRadius: '12px', padding: '12px 14px', borderColor: '#d0d7de' }}
        />
      </div>

      <div className="agent-list" style={{ maxHeight: '492px', overflowY: 'scroll', scrollbarGutter: 'stable', paddingRight: '6px' }}>
        {filteredAgents.length === 0 ? (
          <div className="text-center text-muted py-3">
            No agents found
          </div>
        ) : (
          filteredAgents.map((agent) => {
            const normalizedStatus = String(agent.status || '').trim().toLowerCase();
            const isOnline = Boolean(agent.online);
            const isPending = pendingAgentId === agent.extension;
            const unreadCount = unreadCountsByAgent[getAgentExtension(agent)] || 0;
            return (
              <div
                key={agent.extension}
                className="agent-item mb-2"
                onClick={() => {
                  if (!isPending) {
                    handleSelectAgent(agent);
                  }
                }}
                style={{
                  cursor: isPending ? 'wait' : 'pointer',
                  borderRadius: '14px',
                  padding: '14px 16px',
                  border: selectedAgent?.extension === agent.extension ? '1px solid #0f766e' : '1px solid #e5e7eb',
                  backgroundColor: selectedAgent?.extension === agent.extension ? '#f0fdfa' : '#ffffff',
                  transition: 'all 0.2s ease',
                  opacity: isPending ? 0.7 : 1
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className={`status-indicator me-2 ${isOnline ? 'bg-success' : 'bg-secondary'}`}
                    style={{ width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 }}
                  ></div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold text-dark">
                      {getAgentName(agent)}{getAgentExtension(agent) ? ` (${getAgentExtension(agent)})` : ''}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                      {getAgentMeta(agent)}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {unreadCount > 0 && (
                      <span className="badge rounded-pill bg-danger" aria-label={`${unreadCount} unread messages`}>
                        {unreadCount > 99 ? '99+' : unreadCount} unread
                      </span>
                    )}
                    <span
                      className="badge bg-light text-dark border"
                      style={{ textTransform: 'capitalize', fontWeight: 600 }}
                    >
                      {isPending ? 'Opening...' : (isOnline ? (normalizedStatus || 'online') : 'offline')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ChatAgentSelector;
